import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@roger/ui-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  AlertCircle,
  Check,
  Info,
  Loader2,
  Plus,
  RotateCcw,
  X,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import {
  getQuestionCustomizations,
  getQuestionUsage,
  saveQuestionCustomizations,
  type QuestionCustomizationsQuestion,
  type QuestionCustomizationsResponse,
  type QuestionUsage,
} from '../../services/questionCustomizations';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  partitionThreeTiers,
  type PartitionedQuestionRow,
} from './questionCatalogGrouping';
import { useIsMobile, useMediaQuery } from '../../hooks/useIsMobile';
import { questionDisplayLabels } from './questionDisplayLabel';

const MAX_ADDITIONS = 30;
const QUESTION_ROW_HEIGHT = 44;
const QUESTION_LIST_HEIGHT = 'h-[35rem]';

type VirtualizedQuestionListProps = {
  listId: string;
  label: string;
  rows: PartitionedQuestionRow[];
  renderRow: (row: PartitionedQuestionRow) => React.ReactNode;
  resetScrollKey?: string;
};

function VirtualizedQuestionList({
  listId,
  label,
  rows,
  renderRow,
  resetScrollKey,
}: VirtualizedQuestionListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    // Every row is deliberately one line. Keeping the virtualizer's item size
    // fixed prevents long popover content from changing row placement.
    estimateSize: () => QUESTION_ROW_HEIGHT,
    getItemKey: index => rows[index]?.question.qid ?? index,
    overscan: 5,
  });

  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;
  useEffect(() => {
    virtualizerRef.current.scrollToOffset(0);
  }, [resetScrollKey]);

  return (
    <div
      ref={parentRef}
      aria-label={label}
      className={`${QUESTION_LIST_HEIGHT} min-h-0 overflow-y-auto overscroll-contain rounded-md border`}
      data-testid={`question-list-${listId}`}
      role='list'
      tabIndex={0}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualizer.getVirtualItems().map(virtualItem => {
          const row = rows[virtualItem.index];
          if (!row) return null;
          return (
            <div
              key={row.question.qid}
              data-index={virtualItem.index}
              style={{
                height: `${QUESTION_ROW_HEIGHT}px`,
                left: 0,
                position: 'absolute',
                top: 0,
                transform: `translateY(${virtualItem.start}px)`,
                width: '100%',
              }}
              className='box-border px-2 py-1'
            >
              {renderRow(row)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuestionWording({
  question,
}: {
  question: QuestionCustomizationsQuestion;
}) {
  const wording = question.question || question.description;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const clickOpenedRef = useRef(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const isMobile = useIsMobile();
  const isTouchPrimary = useMediaQuery('(pointer: coarse) and (hover: none)');
  const useDialogDisclosure = isMobile || isTouchPrimary;
  const cancelScheduledClose = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimeoutRef.current = window.setTimeout(() => {
      if (
        !clickOpenedRef.current &&
        document.activeElement !== triggerRef.current
      ) {
        setIsOpen(false);
      }
    }, 100);
  };
  useEffect(() => () => cancelScheduledClose(), []);

  const trigger = (
    <Button
      ref={triggerRef}
      type='button'
      size='icon'
      variant='ghost'
      aria-label={`Show full question wording for ${question.description}`}
      className='shrink-0'
      onFocus={() => {
        if (useDialogDisclosure) return;
        cancelScheduledClose();
        setIsOpen(true);
      }}
      onBlur={() => {
        if (!useDialogDisclosure && !clickOpenedRef.current) scheduleClose();
      }}
      onPointerEnter={() => {
        if (useDialogDisclosure) return;
        cancelScheduledClose();
        setIsOpen(true);
      }}
      onPointerLeave={() => {
        if (!useDialogDisclosure && !clickOpenedRef.current) scheduleClose();
      }}
      onPointerDown={() => {
        cancelScheduledClose();
        clickOpenedRef.current = true;
        setIsOpen(true);
      }}
      onClick={() => {
        cancelScheduledClose();
        clickOpenedRef.current = true;
        setIsOpen(true);
      }}
    >
      <Info className='h-4 w-4' aria-hidden='true' />
      <span className='sr-only'>Full question wording</span>
    </Button>
  );

  if (useDialogDisclosure) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={(nextOpen: boolean) => {
          setIsOpen(nextOpen);
          if (!nextOpen) clickOpenedRef.current = false;
        }}
      >
        {trigger}
        <DialogContent
          className='w-[calc(100vw-1rem)] max-w-lg overflow-y-auto sm:max-w-lg'
          style={{
            maxHeight: 'calc(100dvh - 80px)',
            top: '72px',
            transform: 'translateX(-50%)',
          }}
        >
          <DialogTitle className='sr-only'>Full question wording</DialogTitle>
          <DialogDescription className='sr-only'>
            Full wording for {question.description}
          </DialogDescription>
          <div className='space-y-2 text-sm'>
            <p>{wording}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(nextOpen: boolean) => {
        setIsOpen(nextOpen);
        if (!nextOpen) clickOpenedRef.current = false;
      }}
    >
      <PopoverAnchor asChild>{trigger}</PopoverAnchor>
      <PopoverContent
        align='start'
        // Keep a flipped popover below the app header, and cap it to the space
        // Radix found on the chosen side so long wording remains scrollable.
        collisionPadding={{ top: 64, right: 8, bottom: 8, left: 8 }}
        className='max-h-[min(24rem,var(--radix-popover-content-available-height))] max-w-[calc(100vw-2rem)] overflow-y-auto text-sm'
        role='tooltip'
        onOpenAutoFocus={event => event.preventDefault()}
        onCloseAutoFocus={event => event.preventDefault()}
        // A click on the anchor is not an outside interaction: the trigger
        // keeps the popover open itself.
        onInteractOutside={event => {
          if (triggerRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
        onPointerEnter={cancelScheduledClose}
        onPointerLeave={() => {
          if (!clickOpenedRef.current) scheduleClose();
        }}
      >
        <div className='space-y-2'>
          <p>{wording}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const QuestionCustomizations: React.FC = () => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const [questions, setQuestions] = useState<QuestionCustomizationsQuestion[]>(
    []
  );
  const [defaultQids, setDefaultQids] = useState<string[]>([]);
  const [additionQids, setAdditionQids] = useState<string[]>([]);
  const [questionUsage, setQuestionUsage] = useState<QuestionUsage>(new Map());
  const [usageMetadataFailed, setUsageMetadataFailed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [saved, setSaved] = useState(false);

  const questionLabels = useMemo(
    () => questionDisplayLabels(questions),
    [questions]
  );
  const displayLabel = useCallback(
    (question: QuestionCustomizationsQuestion) =>
      questionLabels.get(question.qid) ?? question.description,
    [questionLabels]
  );
  const defaultQidSet = useMemo(() => new Set(defaultQids), [defaultQids]);
  const selectedQidSet = useMemo(() => new Set(additionQids), [additionQids]);
  const additionCount = additionQids.length;

  const allRows = useMemo(
    () =>
      partitionThreeTiers({
        questions,
        defaultQids,
        additionQids,
        usage: questionUsage,
        searchQuery,
        getDisplayLabel: displayLabel,
      }),
    [
      questions,
      defaultQids,
      additionQids,
      questionUsage,
      searchQuery,
      displayLabel,
    ]
  );

  const applyResponse = useCallback(
    (response: QuestionCustomizationsResponse) => {
      setQuestions(response.questions);
      setDefaultQids(response.default_qids);
      setAdditionQids(response.addition_qids);
      setSaved(false);
    },
    []
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    const [catalogResult, usageResult] = await Promise.allSettled([
      getQuestionCustomizations(),
      getQuestionUsage(),
    ]);

    if (
      catalogResult.status === 'rejected' ||
      usageResult.status === 'rejected'
    ) {
      if (catalogResult.status === 'rejected') {
        console.error(
          'Failed to load question customizations:',
          catalogResult.reason
        );
      }
      if (usageResult.status === 'rejected') {
        console.error(
          'Failed to load question usage metadata:',
          usageResult.reason
        );
      }
      setHasError(true);
      showError(
        'Something went wrong loading question customizations. Please try again.'
      );
      setIsLoading(false);
      return;
    }

    applyResponse(catalogResult.value);
    setQuestionUsage(usageResult.value.usage);
    setUsageMetadataFailed(usageResult.value.hasMetadataFailure);
    setIsLoading(false);
  }, [applyResponse, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const addQuestion = (question: QuestionCustomizationsQuestion) => {
    if (
      !question.available ||
      defaultQidSet.has(question.qid) ||
      selectedQidSet.has(question.qid) ||
      additionCount >= MAX_ADDITIONS
    ) {
      return;
    }
    setSaved(false);
    setAdditionQids(current => [...current, question.qid]);
  };

  const removeQuestion = (qid: string) => {
    if (defaultQidSet.has(qid)) return;
    setSaved(false);
    setAdditionQids(current =>
      current.filter(selectedQid => selectedQid !== qid)
    );
  };

  const save = async () => {
    setIsSaving(true);
    try {
      applyResponse(await saveQuestionCustomizations(additionQids));
      setSaved(true);
      showSuccess('Question customizations saved');
    } catch (error) {
      console.error('Failed to save question customizations:', error);
      showError(
        getBusinessErrorMessage(error) ??
          'Unable to save question customizations. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Question customizations</CardTitle>
          <CardDescription>
            Add questions to your agency&apos;s RogerDocs workflows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
            Loading questions...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (hasError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Question customizations</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <Alert variant='destructive'>
            <div className='flex items-start gap-2'>
              <AlertCircle
                className='mt-0.5 h-4 w-4 shrink-0'
                aria-hidden='true'
              />
              <AlertDescription>
                Something went wrong loading question customizations.
              </AlertDescription>
            </div>
          </Alert>
          <Button
            type='button'
            size='sm'
            variant='outline'
            onClick={() => void load()}
          >
            <RotateCcw className='mr-2 h-4 w-4' aria-hidden='true' />
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='min-w-0'>
      <CardHeader>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div>
            <CardTitle>Question customizations</CardTitle>
            <CardDescription>
              Add up to 30 questions to your agency&apos;s RogerDocs workflows.
            </CardDescription>
          </div>
          <Button
            type='button'
            size='sm'
            onClick={() => void save()}
            disabled={isSaving}
            className='shrink-0'
          >
            {isSaving ? (
              <>
                <Loader2
                  className='mr-2 h-4 w-4 animate-spin'
                  aria-hidden='true'
                />
                Saving...
              </>
            ) : (
              'Save questions'
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className='min-w-0 space-y-5'>
        {saved && (
          <div
            className='flex items-center gap-2 text-sm text-green-700'
            role='status'
          >
            <Check className='h-4 w-4' aria-hidden='true' /> Saved
          </div>
        )}

        <div className='space-y-2'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <p className='text-sm text-muted-foreground'>
              Selected questions appear during visits. Each one requires a
              comment.
            </p>
            <Badge variant='outline' className='whitespace-nowrap'>
              {additionCount} of {MAX_ADDITIONS} additions
            </Badge>
          </div>
        </div>

        <div className='space-y-3'>
          {usageMetadataFailed && (
            <p className='text-xs text-muted-foreground' role='status'>
              Some assessment forms could not be loaded. Catalog questions may
              be incomplete.
            </p>
          )}
          <div className='space-y-1'>
            <label
              htmlFor='oasis-question-search'
              className='text-sm text-muted-foreground sr-only'
            >
              Search OASIS assessment questions
            </label>
            <Input
              id='oasis-question-search'
              inputSize='sm'
              value={searchQuery}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(event.target.value)
              }
              placeholder='Search OASIS assessment questions...'
              aria-label='Search OASIS assessment questions'
            />
          </div>

          {questions.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No questions are available.
            </p>
          ) : allRows.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No matching questions.
            </p>
          ) : (
            <VirtualizedQuestionList
              listId='oasis'
              label='OASIS assessment questions'
              rows={allRows}
              resetScrollKey={searchQuery}
              renderRow={row => {
                const { isSelected, isDefault, question, displayLabel } = row;
                const atLimit = additionCount >= MAX_ADDITIONS;
                const rowText = !question.available
                  ? isSelected
                    ? 'This question is no longer available. Remove it before saving.'
                    : 'This question is no longer available.'
                  : displayLabel;

                return (
                  <div
                    role='listitem'
                    className={`flex min-w-0 items-center gap-2 rounded-md border px-2 py-1 ${
                      isSelected ? 'border-red-500' : 'border-border'
                    }`}
                  >
                    <span className='sr-only'>
                      {isSelected
                        ? 'Selected addition'
                        : isDefault
                          ? 'Default question'
                          : 'Unselected question'}
                    </span>
                    <div className='flex min-w-0 flex-1 items-center gap-1'>
                      <QuestionWording question={question} />
                      <p className='min-w-0 truncate'>{rowText}</p>
                    </div>
                    {isDefault ? (
                      <Badge variant='secondary' className='shrink-0'>
                        Default
                      </Badge>
                    ) : isSelected ? (
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => removeQuestion(question.qid)}
                        aria-label={`Remove question: ${displayLabel}`}
                        className='shrink-0'
                      >
                        <X className='mr-1.5 h-4 w-4' aria-hidden='true' />{' '}
                        Remove
                      </Button>
                    ) : (
                      <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => addQuestion(question)}
                        disabled={!question.available || atLimit}
                        aria-label={`Add question: ${displayLabel}`}
                        className='shrink-0'
                      >
                        <Plus className='mr-1.5 h-4 w-4' aria-hidden='true' />
                        Add
                      </Button>
                    )}
                  </div>
                );
              }}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default QuestionCustomizations;
